import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import axios from 'axios';
import { apiService } from "../../../services/api";
import { Offcanvas, Modal } from 'bootstrap';
import { OpportunityStatus, CreateOpportunityData } from '../../../types/opportunity';
import { Contato } from '../../../types/contato';

const route = all_routes;

interface TempoEntregaOption {
    value: string;
    label: string;
}

export interface Opportunity {
    id: string;
    title: string;
    numero_contrato: string;
    value: string;
    status: string;
    id_status?: number;
    status_e_final?: boolean;
    bidDate: Date;
    contact: string;
    contact_id: number;
    funnel: string;
    funnel_id: number;
    stage: string;
    stage_id: number;
    owner: {
        id: number;
        name: string;
        avatar?: string;
    };
    municipio: {
        id: number;
        nome: string;
        uf: string;
    };
    source: string;
    source_id: number;
    phone: string;
    progress: number;
    bgColor: string;
    url_site_externo: string;
    uasb?: string;
    tempo_entrega?: string;
}

interface OpportunityFilters {
    page: number;
    per_page: number;
    search?: string;
    status?: string;
    statuses?: string[]; // Nova propriedade para múltiplos status
}

export const useOpportunityHandler = (idConta: string) => {
    const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<OpportunityFilters>({
        page: 1,
        per_page: 999999, // ✅ ALTERAÇÃO AQUI
        statuses: ['Aberta', 'Em andamento']
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        total: 0
    });

    const [deletingOpportunityId, setDeletingOpportunityId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [currentOpportunityId, setCurrentOpportunityId] = useState<string | null>(null);

    const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);

    // Novos estados para o contrato
    const [showModalSucessoContrato, setShowModalSucessoContrato] = useState<boolean>(false);
    const [contratoGeradoId, setContratoGeradoId] = useState<number | undefined>(undefined);

    // Adicione uma função para preparar a edição
    const handlePrepareEditOpportunity = (opportunity: Opportunity) => {
        setEditingOpportunity(opportunity);
    };

    // Função para obter a cor de fundo com base na etapa do funil
    const getBgColor = (stage: string): string => {
        const stageColorMap: Record<string, string> = {
            'Prospecção': 'bg-purple',
            'Avaliação': 'bg-warning',
            'Documentação': 'bg-success',
            'Análise': 'bg-danger'
        };
        return stageColorMap[stage] || 'bg-primary';
    };

    // Função para obter a cor com base no status da oportunidade
    const getStatusColor = (statusName: string, eFinal?: boolean): string => {
        if (eFinal) {
            if (statusName === 'Ganha') return 'bg-success';
            if (statusName === 'Perdida') return 'bg-danger';
            if (statusName === 'Cancelada') return 'bg-secondary';
            return 'bg-info'; // Outros status finais
        }
        
        const statusColorMap: Record<string, string> = {
            'Em Andamento': 'bg-info',
            'Aberta': 'bg-warning'
        };
        return statusColorMap[statusName] || 'bg-primary';
    };

    const formatCurrency = (value: string): string => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(Number(value));
    };

    const addNewOpportunity = useCallback((newOpportunity: Opportunity) => {
        setOpportunities(prevOpportunities => {
            const exists = prevOpportunities.some(opp => opp.id === newOpportunity.id);
            if (exists) {
                return prevOpportunities;
            }
            return [newOpportunity, ...prevOpportunities];
        });
    }, []);

    // Modificação na função fetchOpportunities
    const fetchOpportunities = useCallback(async () => {
        try {
            setLoading(true);

            // Preparando os filtros para a API
            let apiFilters: any = {
                id_conta: idConta,
                page: filters.page,
                per_page: filters.per_page,
                search: filters.search,
            };

            // Se temos múltiplos status selecionados, vamos buscar cada um e combinar os resultados
            if (filters.statuses && filters.statuses.length > 0) {
                const allResults: Opportunity[] = [];
                const allPagination = {
                    currentPage: 1,
                    totalPages: 1,
                    total: 0
                };

                // Buscar oportunidades para cada status selecionado
                for (const status of filters.statuses) {
                    const statusFilter = {
                        ...apiFilters,
                        status
                    };

                    try {
                        const opportunitiesResponse = await apiService.getOpportunities(statusFilter);

                        if (opportunitiesResponse.success) {
                            const opportunitiesData = opportunitiesResponse.data || [];
                            const pagination = opportunitiesResponse.pagination || {
                                page: 1,
                                per_page: 999999, // ✅ ALTERAÇÃO AQUI
                                total: 0,
                                total_pages: 1
                            };

                            // Formatar oportunidades com detalhes de contato
                            const formattedOpportunities = await processOpportunities(opportunitiesData);
                            allResults.push(...formattedOpportunities);

                            // Atualizar totais de paginação
                            allPagination.total += pagination.total;
                            allPagination.totalPages = Math.max(allPagination.totalPages, pagination.total_pages);
                        }
                    } catch (statusError) {
                        console.error(`Erro ao buscar oportunidades com status ${status}:`, statusError);
                    }
                }

                setOpportunities(allResults);
                setPagination(allPagination);

            } else if (filters.status) {
                // Comportamento original para um único status
                apiFilters.status = filters.status;

                const opportunitiesResponse = await apiService.getOpportunities(apiFilters);

                if (opportunitiesResponse.success) {
                    const opportunitiesData = opportunitiesResponse.data || [];
                    const pagination = opportunitiesResponse.pagination || {
                        page: 1,
                        per_page: 20,
                        total: 0,
                        total_pages: 1
                    };

                    // Formatar oportunidades com detalhes de contato
                    const formattedOpportunities = await processOpportunities(opportunitiesData);
                    setOpportunities(formattedOpportunities);
                    setPagination({
                        currentPage: pagination.page,
                        totalPages: pagination.total_pages,
                        total: pagination.total
                    });
                }
            } else {
                // Sem filtro de status
                const opportunitiesResponse = await apiService.getOpportunities(apiFilters);

                if (opportunitiesResponse.success) {
                    const opportunitiesData = opportunitiesResponse.data || [];
                    const pagination = opportunitiesResponse.pagination || {
                        page: 1,
                        per_page: 20,
                        total: 0,
                        total_pages: 1
                    };

                    // Formatar oportunidades com detalhes de contato
                    const formattedOpportunities = await processOpportunities(opportunitiesData);
                    setOpportunities(formattedOpportunities);
                    setPagination({
                        currentPage: pagination.page,
                        totalPages: pagination.total_pages,
                        total: pagination.total
                    });
                }
            }
        } catch (err) {
            console.error('Erro detalhado:', err);
            setError(err instanceof Error ? err.message : 'Erro ao buscar oportunidades');

            if (axios.isAxiosError(err) && err.response?.status === 401) {
                console.log('Erro de autenticação - necessário fazer login novamente');
            }
        } finally {
            setLoading(false);
        }
    }, [idConta, filters]);

    // Nova função auxiliar para processar oportunidades
    const processOpportunities = async (opportunitiesData: any[]): Promise<Opportunity[]> => {
        try {
            const opportunitiesWithDetails = await Promise.all(
                opportunitiesData.map(async (opp: any) => {
                    try {
                        if (opp.id_contato) {
                            const contactResponse = await apiService.getContatoById(opp.id_contato);
                            console.log('Resposta do contato:', contactResponse);

                            if (contactResponse.success && Array.isArray(contactResponse.data)) {
                                const contatoEncontrado = contactResponse.data.find(
                                    (contato: Contato) => contato.id === opp.id_contato
                                );

                                //console.log("API opportunity data:", opp);
                                //console.log("API url_site_externo:", opp.url_site_externo, typeof opp.url_site_externo);

                                if (contatoEncontrado) {
                                    const formattedOpportunity: Opportunity = {
                                        id: opp.id?.toString() || '',
                                        title: opp.titulo || 'Sem título',
                                        value: formatCurrency(
                                            (opp.valor_total_proposta !== null &&
                                                opp.valor_total_proposta !== undefined &&
                                                opp.valor_total_proposta > 0)
                                                ? opp.valor_total_proposta
                                                : opp.valor_licitacao
                                        ),
                                        status: opp.status || 'Indefinido',
                                        id_status: opp.id_status,
                                        status_e_final: opp.status_e_final === "1" || opp.status_e_final === 1 || opp.status_e_final === true,
                                        bidDate: opp.data_licitacao ? new Date(opp.data_licitacao) : new Date(),
                                        funnel: opp.funil_nome || '',
                                        funnel_id: opp.id_funil || 0,
                                        stage: opp.etapa_nome || '',
                                        stage_id: opp.id_etapa || 0,
                                        owner: {
                                            id: opp.id_dono || 0,
                                            name: opp.dono_nome || 'Não definido',
                                            avatar: opp.dono_avatar
                                        },
                                        contact: contatoEncontrado?.razao_social || 'Sem contato',
                                        contact_id: opp.id_contato || 0,
                                        source: opp.origem_nome || '',
                                        source_id: opp.id_origem || 0,
                                        phone: contatoEncontrado?.telefone || '',
                                        numero_contrato: opp.numero_contrato || 'Não informado',
                                        municipio: {
                                            id: contatoEncontrado?.id_municipio || 0,
                                            nome: contatoEncontrado?.municipio_nome || '',
                                            uf: contatoEncontrado?.municipio_uf || ''
                                        },
                                        bgColor: getBgColor(opp.etapa_nome || ''),
                                        progress: 0,
                                        url_site_externo: opp.url_site_externo || '',
                                        uasb: opp.uasb || '',
                                        tempo_entrega: opp.tempo_entrega || 'Sem informação'
                                    };

                                    return formattedOpportunity;
                                }
                            }
                            console.log('Contato não encontrado para a oportunidade:', opp.id);
                        }
                        return null;
                    } catch (error) {
                        console.error('Erro ao buscar detalhes do contato:', error);
                        return null;
                    }
                })
            );

            // Filtrar valores nulos e fazer typecast para Opportunity[]
            return opportunitiesWithDetails.filter((opp): opp is Opportunity => opp !== null);
        } catch (error) {
            console.error('Erro ao processar oportunidades:', error);
            return [];
        }
    };

    // Adicione este método ao hook useOpportunityHandler
    const applyStatusFilters = (selectedStatuses: string[]) => {
        console.log('Aplicando filtros de status:', selectedStatuses);

        setFilters(prevFilters => ({
            ...prevFilters,
            statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
            status: selectedStatuses.length === 1 ? selectedStatuses[0] : undefined,
            page: 1 // Resetamos para a primeira página ao aplicar um novo filtro
        }));
    };

    useEffect(() => {
        fetchOpportunities();
    }, [fetchOpportunities]);

    const formatDate = (date: Date): string => {
        const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const day = date.getDate().toString().padStart(2, '0');
        const month = months[date.getMonth()];
        const year = date.getFullYear();

        // Adicionar informações de horário
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');

        return `${day} ${month} ${year} ${hours}:${minutes}`;
    };

    const handleUpdateOpportunity = async (opportunityId: string, updatedData: CreateOpportunityData) => {
        try {
            // Verifica se é para criar um contrato automaticamente
            const criarContrato = updatedData.criarContrato === true;

            // Remove a propriedade extra antes de enviar para a API
            if ('criarContrato' in updatedData) {
                delete (updatedData as any).criarContrato;
            }

            console.log('ID da Oportunidade:' + opportunityId);
            console.log('DEBUG updatedData Opportunidade:', JSON.stringify(updatedData, null, 2));

            // Atualiza a oportunidade
            const response = await apiService.updateOpportunity(Number(opportunityId), updatedData);

            // Log para debug
            console.log('Resposta da atualização:', response);

            // Cria contrato automaticamente se necessário
            let contratoResponse = null;
            if (criarContrato) {
                try {
                    let dataInicio, dataFim;

                    // Usar datas fornecidas ou calcular padrão (1 ano)
                    if (updatedData.contrato_data_inicio && updatedData.contrato_data_fim) {
                        // Datas customizadas do modal
                        dataInicio = updatedData.contrato_data_inicio;
                        dataFim = updatedData.contrato_data_fim;
                    } else {
                        // Padrão: 1 ano a partir de hoje
                        const hoje = new Date();
                        const daquiUmAno = new Date(hoje);
                        daquiUmAno.setFullYear(daquiUmAno.getFullYear() + 1);

                        dataInicio = hoje.toISOString().split('T')[0];
                        dataFim = daquiUmAno.toISOString().split('T')[0];
                    }

                    // CORREÇÃO: Usar a mesma lógica de priorização do valor
                    const valorContrato = (updatedData.valor_total_proposta !== null &&
                        updatedData.valor_total_proposta !== undefined &&
                        updatedData.valor_total_proposta > 0)
                        ? updatedData.valor_total_proposta
                        : updatedData.valor_licitacao;

                    // Dados para criação do contrato
                    const contratoData = {
                        id_oportunidade: Number(opportunityId),
                        id_licitante: updatedData.id_contato,
                        data_inicio: dataInicio,
                        data_finalizacao: dataFim,
                        valor_total: valorContrato, // ALTERADO: usar o valor correto
                        numero_contrato: updatedData.numero_contrato,
                        observacoes: updatedData.titulo || `Contrato gerado automaticamente a partir da oportunidade #${opportunityId}`,
                        descricao: updatedData.titulo
                    };

                    // Chama a API para criar o contrato
                    contratoResponse = await apiService.createContrato(contratoData);
                    console.log('Contrato criado com sucesso:', contratoResponse);

                    // Define a propriedade contratoGerado na resposta
                    response.data.contratoGerado = contratoResponse.data.id;
                } catch (contratoError) {
                    console.error('Erro ao criar contrato:', contratoError);
                }
            }

            // CORREÇÃO: Calcular o valor formatado usando a mesma lógica de priorização
            const valorParaExibir = (response.data.valor_total_proposta !== null &&
                response.data.valor_total_proposta !== undefined &&
                response.data.valor_total_proposta > 0)
                ? response.data.valor_total_proposta
                : (updatedData.valor_total_proposta !== null &&
                    updatedData.valor_total_proposta !== undefined &&
                    updatedData.valor_total_proposta > 0)
                    ? updatedData.valor_total_proposta
                    : updatedData.valor_licitacao;

            const valorFormatado = new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(valorParaExibir);

            // Atualiza a lista de oportunidades
            setOpportunities(prev =>
                prev.map(opp =>
                    opp.id === opportunityId
                        ? {
                            ...opp,
                            title: updatedData.titulo,
                            numero_contrato: updatedData.numero_contrato,
                            value: valorFormatado, // ALTERADO: usar o valor calculado corretamente
                            status: updatedData.status || response.data.status || opp.status,
                            bidDate: new Date(updatedData.data_licitacao),
                            contact_id: updatedData.id_contato,
                            funnel_id: updatedData.id_funil,
                            stage_id: updatedData.id_etapa,
                            source_id: updatedData.id_origem,
                            owner: {
                                id: updatedData.id_dono,
                                name: response.data.dono_nome || opp.owner.name,
                                avatar: opp.owner.avatar
                            },
                            contact: response.data.contato_nome || opp.contact,
                            funnel: response.data.funil_nome || opp.funnel,
                            stage: response.data.etapa_nome || opp.stage,
                            source: response.data.origem_nome || opp.source,
                            publicOrg: response.data.licitante_nome || opp.municipio,
                            publicOrg_id: updatedData.id_municipio,
                            bgColor: getBgColor(response.data.etapa_nome || opp.stage),
                            progress: opp.progress
                        }
                        : opp
                )
            );

            // Atualiza a lista completa de oportunidades
            await fetchOpportunities();

            // Define o ID da oportunidade atual
            setCurrentOpportunityId(opportunityId);

            // IMPORTANTE: Preparamos qual modal mostrar
            if (contratoResponse) {
                // Se um contrato foi criado, mostra o modal específico para contrato
                setContratoGeradoId(contratoResponse.data.id);
                setShowModalSucessoContrato(true);
                setOpenModal(false);
            } else {
                // Caso contrário, mostra o modal normal de sucesso
                setOpenModal(true);
            }

            // MODIFICAÇÃO: Fechar o modal de edição e limpar o estado
            // Importante: Fazemos isso DEPOIS de preparar os modais para evitar o flash sem feedback
            const offcanvasElement = document.getElementById('offcanvas_edit');
            if (offcanvasElement) {
                const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
                if (offcanvasInstance) {
                    offcanvasInstance.hide();
                }
            }

            // Atualiza o estado de editingOpportunity para null
            setEditingOpportunity(null);

            // Finalmente, desativamos o indicador de loading
            // Isso deve acontecer DEPOIS de tudo estar pronto para exibição
            setTimeout(() => {
                // Adicionamos um pequeno delay para garantir que o modal tenha tempo de ser preparado
                if (typeof setLoading === 'function') {
                    setLoading(false);
                }
            }, 300); // Delay de 300ms para garantir que o modal apareça sem um flash da tela vazia

            return response;
        } catch (error) {
            console.error('Erro ao atualizar oportunidade:', error);
            alert('Erro ao atualizar a oportunidade. Por favor, tente novamente.');

            // Em caso de erro, desativamos o loading imediatamente
            if (typeof setLoading === 'function') {
                setLoading(false);
            }

            throw error;
        }
    };

    const handlePrepareDeleteOpportunity = (opportunityId: string) => {
        setDeletingOpportunityId(Number(opportunityId));
    };

    const handleDeleteOpportunity = async () => {
        if (!deletingOpportunityId) return;

        try {
            setIsDeleting(true);
            await apiService.deleteOpportunity(deletingOpportunityId);

            // Atualiza a lista de oportunidades
            setOpportunities(prev => prev.filter(opp => Number(opp.id) !== deletingOpportunityId));

            // Fecha o modal de confirmação
            const deleteModal = document.getElementById('delete_opportunity');
            if (deleteModal) {
                const modalInstance = Modal.getInstance(deleteModal);
                modalInstance?.hide();
            }

            // Mostra modal de sucesso
            const successModal = document.getElementById('success_delete_modal');
            if (successModal) {
                const modalInstance = new Modal(successModal);
                modalInstance.show();
            }
        } catch (error) {
            console.error('Erro ao excluir oportunidade:', error);
            alert('Erro ao excluir a oportunidade. Por favor, tente novamente.');
        } finally {
            setIsDeleting(false);
            setDeletingOpportunityId(null);
        }
    };

    const renderOpportunityCard = (opp: Opportunity) => {
        const initials = opp.title
            .split(' ')
            .map(name => name[0])
            .slice(0, 2)
            .join('')
            .toUpperCase() || 'OO';

        //console.log("Opportunity URL:", opp.url_site_externo, typeof opp.url_site_externo);

        return (
            <div key={opp.id} className="card kanban-card border">
                <div className="card-body">
                    <div className="d-block">
                        <div className={`${opp.bgColor} border border-2 mb-3`} />
                        <div className="d-flex align-items-center mb-3 title">
                            <h6 className="d-flex justify-content-between fw-medium">
                                <Link to={`/crm/oportunidades/detalhes/${opp.id}`}>
                                    {opp.title}
                                </Link>
                            </h6>
                        </div>
                    </div>
                    <div className="mb-1 d-flex flex-column gap-1">
                        <p className="text-default d-inline-flex align-items-center">
                            <i className="ti ti-user text-dark me-1" />
                            {opp.contact}
                        </p>
                        <p className="text-default d-inline-flex align-items-center">
                            <i className="ti ti-code text-dark me-1" />
                            UASG: {opp.uasb || 'Não informado'}
                        </p>
                        <p className="text-default d-inline-flex align-items-center">
                            <i className="ti ti-file-invoice text-dark me-1" />
                            N° do Contrato: {opp.numero_contrato}
                        </p>
                        <p className="text-default d-inline-flex align-items-center">
                            <i className="ti ti-building text-dark me-1" />
                            {opp.source
                                ? `${opp.source}`
                                : 'Não informado'}
                        </p>
                        <p className="text-default d-inline-flex align-items-center">
                            <i className="ti-world text-dark me-1" />
                            {opp.url_site_externo && opp.url_site_externo.trim() !== "" ? (
                                <a
                                    href={/^https?:\/\//i.test(opp.url_site_externo) ? opp.url_site_externo : `https://${opp.url_site_externo}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary text-truncate"
                                    style={{ maxWidth: '180px' }}
                                    title={opp.url_site_externo}
                                >
                                    {opp.url_site_externo.replace(/^https?:\/\//i, '')}
                                </a>
                            ) : (
                                <span className="text-muted">Não informado</span>
                            )}
                        </p>
                        <p className="text-default d-inline-flex align-items-center">
                            <i className="ti ti-report-money text-dark me-1" />
                            {opp.value}
                        </p>
                        <p className="text-default d-inline-flex align-items-center flex-nowrap">
                            <i className="ti-package text-dark me-1" />
                            <span style={{ whiteSpace: 'nowrap' }}>
                                Tempo de entrega:&nbsp;
                                <span className={`badge ${typeof opp.tempo_entrega === 'object' && opp.tempo_entrega !== null
                                    ? ((opp.tempo_entrega as unknown) as TempoEntregaOption).value === 'Única'
                                        ? 'bg-success'
                                        : ((opp.tempo_entrega as unknown) as TempoEntregaOption).value === 'Fracionada'
                                            ? 'bg-warning'
                                            : 'bg-secondary'
                                    : opp.tempo_entrega === 'Única'
                                        ? 'bg-success'
                                        : opp.tempo_entrega === 'Fracionada'
                                            ? 'bg-warning'
                                            : 'bg-secondary'
                                    }`}>
                                    {typeof opp.tempo_entrega === 'object' && opp.tempo_entrega !== null
                                        ? ((opp.tempo_entrega as unknown) as TempoEntregaOption).label
                                        : opp.tempo_entrega || 'Sem informação'}
                                </span>
                            </span>
                        </p>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <Link
                                to="#"
                                className="avatar avatar-xs avatar-rounded avatar-gray flex-shrink-0 me-2"
                            >
                                {opp.owner.avatar ? (
                                    <ImageWithBasePath
                                        src={opp.owner.avatar}
                                        alt={opp.owner.name}
                                    />
                                ) : (
                                    <span className="avatar-title">
                                        {opp.owner.name.charAt(0)}
                                    </span>
                                )}
                            </Link>
                            <Link
                                to="#"
                                className="text-default"
                            >
                                {opp.owner.name}
                            </Link>
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between border-top pt-3 mt-3">
                        <span>
                            <i className="ti ti-calendar-due" /> {formatDate(opp.bidDate)}
                        </span>
                        <span className={`badge ${getStatusColor(opp.status, opp.status_e_final)}`}>
                            {opp.status}
                        </span>
                    </div>
                    <div className="d-flex gap-1 mt-3">
                        <button
                            className="btn btn-success btn-sm mr-3"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_edit"
                            onClick={() => handlePrepareEditOpportunity(opp)}
                        >
                            Editar
                        </button>
                        <button
                            className="btn btn-danger btn-sm"
                            data-bs-toggle="modal"
                            data-bs-target="#delete_opportunity"
                            onClick={() => handlePrepareDeleteOpportunity(opp.id)}
                        >
                            Excluir
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return {
        opportunities,
        loading,
        error,
        pagination,
        setOpportunities,
        addNewOpportunity,
        setFilters,
        renderOpportunityCard,
        refreshOpportunities: fetchOpportunities,
        handlePrepareDeleteOpportunity,
        handleDeleteOpportunity,
        deletingOpportunityId,
        isDeleting,
        showSuccessModal,
        setShowSuccessModal,
        currentOpportunityId,
        setCurrentOpportunityId,
        handleUpdateOpportunity,
        editingOpportunity,
        setEditingOpportunity,
        applyStatusFilters,
        // Novos estados
        showModalSucessoContrato,
        setShowModalSucessoContrato,
        contratoGeradoId,
        setContratoGeradoId
    };
};